/**
 * Achievements — the medal cabinet, personal bests and race history.
 *
 * The hero label wears the brand accent rather than a status colour: this
 * screen is about a personal best, not an action to take.
 */

import {
  bibNo,
  formatDate,
  formatTime,
  isPersonalBest,
  medalsFor,
  memberResults,
  totalKm,
} from '../../data';
import { useClubData } from '../../context/ClubContext';
import { PbGrid } from '../../components/Bib';
import { MedalCabinet } from '../../components/Medals';
import { RaceRow } from '../../components/EventCard';
import { Empty, Eyebrow } from '../../components/ui';

export function AchievementsPage() {
  const { db, me } = useClubData();
  if (!me) return <Empty title="No member">Sign in again to see your achievements.</Empty>;

  const medals = medalsFor(db.results, db.volunteer, me.id);
  const earned = medals.filter((m) => m.on).length;
  const races = memberResults(db.results, me.id);

  return (
    <>
      <div className="relative mb-3.5 overflow-hidden border border-line bg-white p-6">
        <div className="font-market text-[9.5px] uppercase tracking-[0.18em] text-gold-800">
          Bib {bibNo(me.bib)} · {me.name}
        </div>
        <div className="mt-2 font-market text-6xl font-extrabold leading-[0.9] tabular-nums">
          {earned}
          <span className="text-2xl text-copy-label"> / {medals.length}</span>
        </div>
        <div className="mt-2 text-[12.5px] text-copy-muted">
          Medals earned · {totalKm(db.results, me.id).toFixed(0)} km raced across {races.length}{' '}
          races
        </div>
      </div>

      <Eyebrow>Medal cabinet</Eyebrow>
      <MedalCabinet medals={medals} />

      <Eyebrow>Personal bests</Eyebrow>
      <PbGrid memberId={me.id} results={db.results} />

      <Eyebrow>Race history</Eyebrow>
      {races.length ? (
        races.map((r) => (
          <RaceRow
            key={r.id}
            race={r.race}
            meta={`${formatDate(r.date)} · ${r.dist}`}
            time={formatTime(r.timeS)}
            isPb={isPersonalBest(db.results, me.id, r.dist, r.timeS, r.id)}
          />
        ))
      ) : (
        <Empty title="No races yet">Your first result will appear here.</Empty>
      )}
    </>
  );
}
